/** Pure helpers that drive the toolbar / sub-toolbar disabled
 *  states (issue #150). Extracted from `app.ts` so the gating
 *  logic is unit-testable without standing up the full editor.
 *
 *  The flow at runtime:
 *    1. `syncToolAvailability` runs on every commit + on startup.
 *    2. For each top-level toolbar entry it asks
 *       `topbarToolDisabled` whether the parent button should be
 *       disabled.
 *    3. For each Leather / Stamping subtool button it asks
 *       `partRequiringSubtoolDisabled`.
 *    4. For each Construction subtool button it asks
 *       `constructionSubtoolDisabled`.
 *
 *  Disabled buttons swallow clicks (HTML semantics); keyboard
 *  shortcuts that bypass the DOM go through the matching guard
 *  inside `activateTool` / `setConstructionSubMode`. */

export interface TopbarToolEntry {
  id: string;
  /** Set on parent groups (Leather, Stamping). Absent on
   *  stand-alone tools (Construction, Hardware) whose top-level
   *  button IS the tool itself. */
  defaultSubtool?: string;
}

/** Top-level toolbar button — disabled when the tool it represents
 *  (or, for parent groups, the default subtool it activates on
 *  click) needs a leather part and the project has none. */
export function topbarToolDisabled(
  entry: TopbarToolEntry,
  toolsRequiringPart: ReadonlySet<string>,
  hasParts: boolean,
): boolean {
  const probe = entry.defaultSubtool ?? entry.id;
  return toolsRequiringPart.has(probe) && !hasParts;
}

/** Leather / Stamping subtool button — disabled when its
 *  underlying tool needs a part and the project has none. */
export function partRequiringSubtoolDisabled(
  id: string,
  toolsRequiringPart: ReadonlySet<string>,
  hasParts: boolean,
): boolean {
  return toolsRequiringPart.has(id) && !hasParts;
}

/** Construction subtool button — `draw` is always usable; `select`
 *  acts on construction lines OR tracing images (issue #509 — images
 *  are selectable / movable in the Select sub-mode), so it's enabled
 *  when either exists; the geometry-editing sub-tools (edit / offset /
 *  round) only act on construction lines and stay disabled until at
 *  least one is drawn. */
export function constructionSubtoolDisabled(
  id: string,
  hasConstructionLines: boolean,
  hasTracingImages = false,
): boolean {
  if (id === "draw") return false;
  if (id === "select") return !hasConstructionLines && !hasTracingImages;
  return !hasConstructionLines;
}
